import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PortfolioContainer = styled.div`
  min-height: 100vh;
  padding: 6rem 2rem 4rem;
  background: linear-gradient(135deg, #f5f0eb 0%, #faf8f5 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: 300;
  color: #d4737d;
  margin: 0 0 4rem 0;
  font-family: 'Playfair Display', serif;
  letter-spacing: -0.02em;
  text-align: center;
`;

const WorkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
  max-width: 1200px;
  width: 100%;
  /* don't stretch items to the same height — align at the top */
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const WorkItem = styled(motion.div)`
  background: white;
  border-radius: 25px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  /* ensure individual items don't stretch to match row height */
  align-self: start;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const WorkImage = styled.div`
  width: 100%;
  /* increased height so images aren't cropped on desktop */
  height: 360px;
  background: linear-gradient(135deg, #d4737d 0%, #b85862 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  font-weight: 500;
  img {
    width: 100%;
    height: 100%;
    /* show the entire image without cropping */
    object-fit: contain;
    object-position: center;
    background: #f7f5f4;
  }
`;

// Presentation wrapper overrides the fixed WorkImage height so embedded PDFs can
// use viewport-based heights and remain readable on mobile/desktop.
const PresentationWrapper = styled(WorkImage)`

  /* remove the WorkImage gradient background for presentations so there
     isn't a visible colored strip below images on certain aspect ratios */
  background: transparent;
  height: auto;
  min-height: 360px;
  display: block; /* allow iframe to control its own height */

  iframe {
    display: block;
    width: 100%;
    height: min(72vh, 900px);
    border: none;
  }

  @media (max-width: 768px) {
    iframe {
      height: min(72vh, 720px);
    }
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
`;

const CarouselImg = styled.img`
  max-width: 100%;
  /* limit image height so it doesn't reveal underlying layout gaps */
  max-height: min(64vh, 720px);
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  display: block;
  transition: opacity 200ms ease;
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.85);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(0,0,0,0.12);

  &:focus { outline: none; }
`;

const PrevButton = styled(CarouselButton)`
  left: 12px;
`;

const NextButton = styled(CarouselButton)`
  right: 12px;
`;

const Indicators = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => (props.active ? '#d4737d' : 'rgba(0,0,0,0.15)')};
  display: inline-block;
`;

const WorkContent = styled.div`
  padding: 2rem;
`;

const WorkTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 500;
  color: #333;
  margin: 0 0 1rem 0;
  font-family: 'Playfair Display', serif;
`;

const WorkDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
  margin: 0;
  font-family: 'Cormorant Garamond', serif;
`;

const Portfolio = () => {
  // Simple carousel component for the Chatelier internship card
  const ChatelierCarousel = () => {
    const [active, setActive] = useState(0);
    const images = ['/chatelier1.png', '/chatelier2.png', '/chatelier3.png', '/chatelier4.png', '/chatelier5.png'];

    const prev = () => setActive((active - 1 + images.length) % images.length);
    const next = () => setActive((active + 1) % images.length);

    return (
      <CarouselContainer>
        <CarouselImg src={images[active]} alt={`Chatelier ${active + 1}`} />
        <PrevButton onClick={prev} aria-label="Previous slide">◀</PrevButton>
        <NextButton onClick={next} aria-label="Next slide">▶</NextButton>
        <Indicators>
          {images.map((_, i) => (
            <Dot key={i} active={i === active} />
          ))}
        </Indicators>
      </CarouselContainer>
    );
  };

  // Carousel for the Aesop project. Uses public/aesop/aesop{1..n}.png by default.
  const AesopCarousel = () => {
    // First item is a vertical video in public/aesop/aesop-vid.mov.
    // After that we show the image slides.
    const imagePaths = [
      '/aesop/1.jpg','/aesop/2.jpg','/aesop/3.jpg','/aesop/4.jpg','/aesop/5.jpg',
      '/aesop/6.jpg','/aesop/7.jpg','/aesop/8.jpg','/aesop/9.jpg','/aesop/10.jpg',
      '/aesop/11.jpg','/aesop/12.jpg','/aesop/13.jpg','/aesop/14.jpg','/aesop/15.jpg'
    ];

    // Build an items array where the first item is the video, then images.
    const items = [
      { type: 'video', src: '/aesop/aesop-vid.mov' },
      ...imagePaths.map((p) => ({ type: 'image', src: p }))
    ];

    const videoRef = useRef(null);

    const [active, setActive] = useState(0);
    const [touchStart, setTouchStart] = useState(null);

    const prev = () => setActive((s) => (s - 1 + items.length) % items.length);
    const next = () => setActive((s) => (s + 1) % items.length);

    // Basic touch handlers for swipe support on mobile
    const onTouchStart = (e) => {
      const touch = e.touches && e.touches[0];
      if (touch) setTouchStart(touch.clientX);
    };

    const onTouchMove = (e) => {
      if (!touchStart) return;
      const touch = e.touches && e.touches[0];
      if (!touch) return;
      const diff = touch.clientX - touchStart;
      // if user swipes more than 40px, navigate
      if (Math.abs(diff) > 40) {
        if (diff > 0) prev(); else next();
        setTouchStart(null);
      }
    };

    const current = items[active];

    return (
      <PresentationWrapper>
        <CarouselContainer
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
        >
          {current.type === 'video' ? (
            <video
              ref={videoRef}
              src={current.src}
              controls
              playsInline
              onLoadedMetadata={() => {
                try {
                  const v = videoRef.current;
                  // ensure first frame is visible: seek to 0 and pause
                  if (v) {
                    // some browsers require play before seeking; try-safe sequence
                    v.currentTime = 0;
                    v.pause();
                  }
                } catch (err) {
                  // ignore seek errors
                }
              }}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <CarouselImg
              src={current.src}
              alt={`Aesop ${active}`}
              onError={(e) => {
                try {
                  const el = e.target;
                  if (!el.dataset.fallbackTried) {
                    el.dataset.fallbackTried = 'true';
                    // fallback from .tiff -> .png if needed
                    el.src = current.src.replace(/\.tiff?$/i, '.png');
                  }
                } catch (err) {
                  // ignore
                }
              }}
            />
          )}

          <PrevButton onClick={prev} aria-label="Previous slide">◀</PrevButton>
          <NextButton onClick={next} aria-label="Next slide">▶</NextButton>

          <Indicators>
            {items.map((_, i) => (
              <Dot key={i} active={i === active} />
            ))}
          </Indicators>
        </CarouselContainer>
      </PresentationWrapper>
    );
  };
  const works = [
    {
      title: "Aesop Brand Strategy University Project",
      description: "As a part of the course, I created a sound strategy for Aesop, exploring how audio can strengthen brand’s identity and emotional connection with its audience"
    },
    {
      title: "Chatelier Internship",
      description: "Created visual and social media content for Chatelier's luxurious skincare brand, supported by research on trends and competitors. "
    },

  ];

  return (
    <PortfolioContainer id="portfolio">
      <Title>Portfolio</Title>
      <WorkGrid>
        {works.map((work, index) => (
          <WorkItem
            key={index}
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            {index === 0 ? (
              <PresentationWrapper>
                <AesopCarousel />
              </PresentationWrapper>
            ) : index === 1 ? (
              <WorkImage>
                <ChatelierCarousel />
              </WorkImage>
            ) : (
              <WorkImage>
                Portfolio Image {index + 1}
              </WorkImage>
            )}
            <WorkContent>
              <WorkTitle>{work.title}</WorkTitle>
              <WorkDescription>{work.description}</WorkDescription>
            </WorkContent>
          </WorkItem>
        ))}
      </WorkGrid>
    </PortfolioContainer>
  );
};

export default Portfolio;